import { useCallback } from 'react';
import { selectIsLockedContentReadOnly } from 'features/layout/features/content/contentSlice';
import { UserScreenshareSource } from 'features/layout/features/order/types';
import { PermissionTypes } from 'features/permissions/types';
import { usePermissions } from 'features/permissions/usePermissions';
import { selectLocalUserId } from 'features/users/usersSlice';
import { useAppSelector } from 'store/hooks';

/**
 * Returns a predicate deciding whether the local user may stop a given screenshare.
 * A user can always stop their own screenshare; stopping someone else's requires the
 * `manageScreenshare` permission for that owner's role. Mirrors the gating of the
 * single-screenshare close control in `useScreensharePanelCloseControl`, but evaluated
 * per stream so the dropdown list can show the close button only where allowed.
 */
export const useCanRemoveScreenshare = () => {
  const { hasPermissions } = usePermissions();
  const localUserId = useAppSelector(selectLocalUserId);
  const isLockedContentReadOnly = useAppSelector(selectIsLockedContentReadOnly);

  return useCallback(
    (screenshare?: UserScreenshareSource) => {
      if (!screenshare) {
        return false;
      }

      const isLocalScreenshare = screenshare.userId === localUserId;
      const canManageScreenshare = hasPermissions(PermissionTypes.manageScreenshare, {
        targetRole: screenshare.user?.role,
      });

      return (canManageScreenshare || isLocalScreenshare) && !isLockedContentReadOnly;
    },
    [hasPermissions, localUserId, isLockedContentReadOnly]
  );
};
